//#region types
import { MarkdownLinkPattern } from "./agentFaqLinks";
//#endregion

/** The agent's Markdown, limited to bold, bullet lists, line breaks and links to the FAQ articles it used. */
export type AnswerBlock = { kind: "paragraph"; lines: string[] } | { kind: "list"; items: string[] };

/** One of the options the agent listed when it asked which one. `text` is what goes back to it. */
export interface AgentChoice {
  title: string;
  description?: string;
  text: string;
}

/** The agent's "which one?" turn, split around its list. */
export interface AgentClarification {
  lead: string;
  choices: AgentChoice[];
  footnote: string;
}

const ListItemPattern = /^\s*[-*•]\s+/;
export const BoldPattern = /\*\*(.+?)\*\*/g;
const LeadingBoldPattern = /^\*\*(.+?)\*\*\s*[-–—,:·]?\s*/;

export const toBlocks = (text: string): AnswerBlock[] =>
  text.split(/\r?\n/).reduce<AnswerBlock[]>((blocks, rawLine) => {
    const line = rawLine.trimEnd();
    const last = blocks.at(-1);
    if (!line.trim()) {
      if (last) blocks.push({ kind: "paragraph", lines: [] });
      return blocks;
    }
    if (ListItemPattern.test(line)) {
      const item = line.replace(ListItemPattern, "");
      if (last?.kind === "list") last.items.push(item);
      else blocks.push({ kind: "list", items: [item] });
      return blocks;
    }
    if (last?.kind === "paragraph") last.lines.push(line);
    else blocks.push({ kind: "paragraph", lines: [line] });
    return blocks;
  }, []);

const toMarkdown = (blocks: AnswerBlock[]): string =>
  blocks
    .map((block) =>
      block.kind === "list" ? block.items.map((item) => `- ${item}`).join("\n") : block.lines.join("\n")
    )
    .filter((part) => part.trim())
    .join("\n\n");

// A leading bold part is the option's name and the rest describes it, the way the agent writes a match.
// An item with a link is not offered: a link cannot sit inside a pressable row.
const toChoice = (item: string): AgentChoice | undefined => {
  // `search`, not `test`: the pattern is global, so `test` would carry its position into the next item.
  if (item.search(MarkdownLinkPattern) >= 0) return undefined;
  const text = item.replace(BoldPattern, "$1").trim();
  if (!text) return undefined;

  const leading = item.match(LeadingBoldPattern);
  const description = leading ? item.slice(leading[0].length).replace(BoldPattern, "$1").trim() : "";
  return leading && description ? { title: leading[1].trim(), description, text } : { title: text, text };
};

// More than this reads as a list to study; anything past it she writes in her own words under "Ingen av dessa".
const MaxChoices = 3;

export const clarificationOf = (text: string): AgentClarification => {
  const blocks = toBlocks(text);
  const listIndex = blocks.findIndex((block) => block.kind === "list");
  if (listIndex < 0) return { lead: text.trim(), choices: [], footnote: "" };

  const list = blocks[listIndex] as Extract<AnswerBlock, { kind: "list" }>;
  const choices = list.items.flatMap((item) => toChoice(item) ?? []);
  // An option left out would vanish from the question, so with any left out she reads it all and answers in words.
  if (choices.length < list.items.length) return { lead: text.trim(), choices: [], footnote: "" };
  return {
    lead: toMarkdown(blocks.slice(0, listIndex)),
    choices: choices.slice(0, MaxChoices),
    footnote: toMarkdown(blocks.slice(listIndex + 1)),
  };
};
